import { get } from 'lodash'

import { Colors, Services, getServiceNameViaPoint, getServicePathToAPI, isEmpty, joinURLPath, reportManagerFieldToServiceMap, reportManagerFilterByOwnerFieldKeys, reportManagerFilterBySchema } from '@sigma-frontend-libs/commons'
import { Macro } from '@sigma-legacy-libs/cache'
import { isUUID } from '@sigmasms/commons'

import { icons, macroCacheOptions, permissionPresets, projectName, request } from '@/utils'

const Cache = new Macro(macroCacheOptions)
const cachedGet = Cache.wrapWithCache(async (key, service, id) => {
  const pathToAPI = getServicePathToAPI(service)

  return await request.get(joinURLPath(pathToAPI, id))
})

export const entityFields = {
  computed: {
    fieldsByService() {
      switch (this.service) {
        case Services['report-manager/queue']: {
          return [ 'id', 'name', 'type', 'runCount', 'failureCount' ]
        }
        case Services['report-manager/recurrent']: {
          return [ 'id', 'name', 'type' ]
        }
      }
    },

    fields() {
      return this.fieldsByService.reduce((result, field) => {
        switch (field) {
          case 'type': {
            result[field] = this.getTranslate(`${getServiceNameViaPoint(this.service)}.labels.${this.value[field]}`)
            break
          }
          default: {
            result[field] = this.value[field]
            break
          }
        }

        return result
      }, {})
    }
  }
}

export const filterBy = {
  computed: {
    /**
     * Определяет, содержит ли фильтр хотя бы одно значимое значение.
     *
     * @returns {boolean}
     */
    filterByIsEmpty() {
      if (isEmpty(this.proxy.filterBy)) {
        return true
      }

      return Object.values(this.proxy.filterBy).every(value => {
        if (Array.isArray(value)) {
          return value.length === 0
        }

        return isEmpty(value)
      })
    },
    /**
     * Проверяет, заполнены ли обязательные поля для выбранного типа отчёта.
     *
     * @returns {boolean}
     */
    filterByRequiredFieldsIsEmpty() {
      if (!this.proxy.type) {
        return true
      }

      const requiredFields = reportManagerFilterBySchema?.[this.proxy.type]?.required ?? []
      if (requiredFields.length === 0) {
        return false
      }

      if (!this.proxy.filterBy) {
        return true
      }

      return requiredFields.some(field => {
        return isEmpty(this.proxy.filterBy[field])
      })
    },
    /**
     * Состояние подписи активатора filterBy: ключ подсказки `hints.filterBy.<state>`.
     *
     * @returns {'empty' | 'set'}
     */
    filterByHintState() {
      if (this.filterByIsEmpty) {
        return 'empty'
      }

      return 'set'
    },
    /**
     * Цвет подписи активатора filterBy: ошибка при незаполненных обязательных полях,
     * успех при заполненном фильтре, иначе серый.
     *
     * @returns {string}
     */
    filterByLabelColor() {
      if (this.proxy.type && this.filterByRequiredFieldsIsEmpty) {
        return Colors.error
      }
      if (!this.filterByIsEmpty) {
        return Colors.success
      }

      return Colors.grey
    },
    filterByActivatorButton() {
      return {
        icon: icons.clear,
        disabled: this.filterByIsEmpty,
        callback: () => {
          this.resetFilterBy(this.proxy.type)
          this.$nextTick(() => {
            return this.resetFilterBy(this.proxy.type)
          })
        }
      }
    }
  },

  watch: {
    'proxy.type'(to, from) {
      this.resetFilterBy(from)
      this.resetFilterBy(to)
    }
  },

  methods: {
    /**
     * Сбрасывает поля фильтра, которые не входят в схему текущего типа отчёта.
     * Это защищает форму от скрытых старых значений после смены типа.
     *
     * @param {string | undefined} type
     * @returns {void}
     */
    resetFilterBy(type) {
      if (!type || !this.proxy.filterBy) {
        return
      }

      const fieldsSchema = reportManagerFilterBySchema?.[type]?.properties || {}
      const nextFilterBy = Object.keys(this.proxy.filterBy).reduce((result, key) => {
        if (!Object.prototype.hasOwnProperty.call(fieldsSchema, key)) {
          result[key] = this.proxy.filterBy[key]
        }

        return result
      }, {})

      this.$set(this.proxy, 'filterBy', nextFilterBy)
    }
  }
}

export const filterByData = {
  props: {
    value: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },

  data() {
    return {
      fetchedItems: {},
      loadingItems: []
    }
  },

  computed: {
    /**
     * Собирает только видимые и непустые поля фильтра для readonly-представления.
     *
     * @returns {Record<string, unknown>}
     */
    items() {
      const result = {}
      const filterBy = this.value?.filterBy ?? {}
      for (const key in filterBy) {
        if (isEmpty(filterBy[key])) {
          continue
        }
        if (Array.isArray(filterBy[key]) && !filterBy[key].length) {
          continue
        }
        if (this.hasPermission(key)) {
          result[key] = this.formatValue(key, filterBy[key])
        }
      }

      return result
    },

    hasItems() {
      return Boolean(Object.keys(this.items).length)
    },

    loading() {
      return this.loadingItems.length > 0
    }
  },

  mounted() {
    this.fetchItems()
  },

  methods: {
    /**
     * Преобразует значение фильтра в человекочитаемый вид для карточки.
     *
     * @param {string} key
     * @param {unknown} value
     * @returns {unknown}
     */
    formatValue(key, value) {
      switch (key) {
        case 'ResellerId':
        case 'OwnerId':
        case 'ownerId':
        case 'widgetId':
        case 'patternId':
        case 'provider': return this.fetchedItems[key] || value
        case 'isActive': return this.getTranslate(`misc.${value}`)
        case 'status': return this.getTranslate(`${Services.sendings}.statuses.${value}`)
        case 'type': {
          if (Array.isArray(value) && value.length) {
            return value.map(type => {
              return this.getTranslate(`${Services.sendings}.types.${type}`)
            }).join(' | ')
          }

          return this.getTranslate(`${Services.sendings}.types.${value}`)
        }
        default: return value
      }
    },

    hasPermission(key) {
      if (reportManagerFilterByOwnerFieldKeys.has(key)) {
        return this.checkPermissions(`advanced.${Services.users}.get`, permissionPresets.resellerUp)
      }
      if (key in reportManagerFieldToServiceMap) {
        return this.checkPermissions(`advanced.${getServiceNameViaPoint(reportManagerFieldToServiceMap[key])}.get`, permissionPresets.meUp)
      }

      return true
    },

    /**
     * Возвращает параметры запроса для гидрации справочного значения фильтра.
     *
     * @param {string} key
     * @returns {{ service: string, pathToTitle: string } | undefined}
     */
    getParams(key) {
      if (reportManagerFilterByOwnerFieldKeys.has(key)) {
        return {
          service: Services.users,
          pathToTitle: 'username'
        }
      }

      if (key in reportManagerFieldToServiceMap) {
        let pathToTitle = 'name'
        if (key === 'provider' || key === 'patternId') {
          pathToTitle = 'title'
        }

        return {
          service: reportManagerFieldToServiceMap[key],
          pathToTitle
        }
      }

      return
    },

    setLoadingKeys(key) {
      if (this.loadingItems.includes(key)) {
        this.loadingItems = this.loadingItems.filter(item => {
          return item !== key
        })
      } else {
        this.loadingItems.push(key)
      }
    },

    async getData(value, service, pathToTitle) {
      if (isUUID(value)) {
        /**
         * Загружает подписи для всех значений filterBy, которые требуют отдельного запроса.
         *
         * @returns {Promise<void>}
         */
        try {
          const key = [ projectName, service, 'get', value ].filter(Boolean).join(':')
          const data = await cachedGet(key, service, value)

          return get(data, pathToTitle, value)
        } catch {
          return value
        }
      }

      return value
    },

    async fetchItem(key, value, params) {
      this.setLoadingKeys(key)

      try {
        const fetchedValue = await this.getData(
          value,
          params.service,
          params.pathToTitle
        )

        if (fetchedValue) {
          this.$set(this.fetchedItems, key, fetchedValue)
        }
      } finally {
        this.setLoadingKeys(key)
      }
    },

    async fetchItems() {
      const filterBy = this.value?.filterBy ?? {}
      const promises = []

      for (const key in filterBy) {
        if (!this.hasPermission(key)) {
          continue
        }
        const params = this.getParams(key)
        if (!params) {
          continue
        }

        promises.push(this.fetchItem(key, filterBy[key], params))
      }

      await Promise.allSettled(promises)
    }
  }
}
